import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SectionHeading from './SectionHeading';
import { FaBuilding, FaUsers, FaTrophy, FaCalendarAlt } from 'react-icons/fa';

const StatsSection = () => {
  const stats = [
    {
      icon: FaCalendarAlt,
      number: "26",
      label: "Years of Experience",
      color: "from-brand-yellow to-brand-gold",
    },
    {
      icon: FaBuilding,
      number: "150+",
      label: "Projects Completed",
      color: "from-brand-yellow to-brand-gold",
    },
    {
      icon: FaUsers,
      number: "50+",
      label: "Satisfied Clients",
      color: "from-brand-yellow to-brand-gold",
    },
    {
      icon: FaTrophy,
      number: "100%",
      label: "Quality Assurance",
      color: "from-brand-yellow to-brand-gold",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="relative bg-gradient-to-br from-brand-navy via-brand-navy-dark to-brand-navy py-16 md:py-24 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full" 
             style={{
               backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)`,
             }}
        />
      </div>

      {/* Animated Background Circles */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-10 left-10 w-64 h-64 bg-brand-navy-light rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute bottom-10 right-10 w-64 h-64 bg-brand-yellow/60 rounded-full blur-3xl"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Our Track Record"
          title="Building Trust Through Numbers"
          subtitle="Our track record speaks for itself - delivering excellence in every project"
          light
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
                className="relative group"
              >
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center border border-white/20 shadow-2xl hover:shadow-3xl transition-all duration-300">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}
                  >
                    <Icon className="text-3xl sm:text-4xl text-brand-navy" />
                  </motion.div>

                  {/* Number with counter animation */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                    className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-2"
                  >
                    {stat.number}
                  </motion.div>

                  {/* Label */}
                  <p className="text-blue-100 text-sm sm:text-base font-medium">
                    {stat.label}
                  </p>

                  {/* Hover effect glow */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-20 rounded-2xl blur-xl transition-opacity duration-300`} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12 md:mt-16"
        >
          <Link
            to="/contact-us"
            className="inline-block bg-brand-yellow text-brand-navy hover:bg-brand-gold font-bold py-3.5 px-9 rounded-full shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300"
          >
            Start Your Project Today
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default StatsSection;

